import { FetchParams } from '../redux/slices/employeesSlice';

/**
 * Builds a query string for Strapi API requests with pagination, filtering, and sorting
 * 
 * @param params The parameters for the API request
 * @returns A query string ready to be appended to a URL
 */
export const buildQueryString = (params: FetchParams): string => {
    const { page = 0, pageSize = 10, sort, filters, populate = '*' } = params;
    
    // Start with pagination and populate
    let queryString = `?populate=${populate}&pagination[page]=${page + 1}&pagination[pageSize]=${pageSize}`;
    
    // Add sorting if provided
    if (sort) {
        queryString += `&sort=${sort}`;
    }
    
    // Add filters if provided
    if (filters) {
        // Process filters recursively
        const addFilters = (prefix: string, filterObj: any) => {
            for (const [key, value] of Object.entries(filterObj)) {
                if (value !== null && value !== undefined) {
                    if (Array.isArray(value)) {
                        // Handle array values (like $or array)
                        value.forEach((item, index) => {
                            if (typeof item === 'object' && item !== null) {
                                // Nested objects in arrays (like items in $or)
                                addFilters(`${prefix}[${key}][${index}]`, item);
                            } else {
                                // Simple array values
                                queryString += `&${prefix}[${key}][${index}]=${encodeURIComponent(String(item))}`;
                            }
                        });
                    } else if (typeof value === 'object' && value !== null) {
                        // Check if the object contains operators like $eq, $gt, etc.
                        const hasOperators = Object.keys(value).some(k => k.startsWith('$'));
                        
                        if (hasOperators) {
                            // If it has operators, preserve them exactly as is
                            for (const [opKey, opValue] of Object.entries(value)) {
                                queryString += `&${prefix}[${key}][${opKey}]=${encodeURIComponent(String(opValue))}`;
                            }
                        } else {
                            // Handle nested objects without operators
                            addFilters(`${prefix}[${key}]`, value);
                        }
                    } else {
                        // Handle simple key-value pairs - default to $containsi for string matching
                        // Direct string values inside $or need to use $containsi
                        if (prefix.includes('[$or]') || prefix.includes('$or')) {
                            queryString += `&${prefix}[${key}][$containsi]=${encodeURIComponent(String(value))}`;
                        } else {
                            queryString += `&${prefix}[${key}][$containsi]=${encodeURIComponent(String(value))}`;
                        }
                    }
                }
            }
        };
        
        addFilters('filters', filters);
    }
    
    return queryString;
}; 